import { AfterViewInit, Component, inject, OnInit, signal, ViewChild } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatSortModule } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { MatButtonModule } from '@angular/material/button';
import { Store } from '@ngxs/store';
import { WidgetMessageService } from '../../core/services/widget-message.service';
import { MatDialog } from '@angular/material/dialog';
import { BehaviorSubject, combineLatest, firstValueFrom, from, Observable, switchMap, tap } from 'rxjs';
import { GlobalMetaDataEntityService } from '../services/global-metadata.entity.service';
import { GlobalMetaDataEntity } from '../global-metadata.entity';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { map } from 'rxjs/operators';
import { EntityStateModel, InvalidateLoadedQueries } from '../../core/state/entity.state';
import { makeQueryKey } from '../../utils/entity-states/query-key';
import { FreeFilterInputComponent } from '../../utils/component/free-filter-input/free-filter-input.component';
import { getMinFilterLengthText } from '../../core/util';
import { AsyncPipe } from '@angular/common';
import { MatTooltip } from '@angular/material/tooltip';
import {
  FormDialogGlobalMetadataComponent
} from '../form-dialog-global-metadata/form-dialog-global-metadata.component';
import {
  ConfirmDialogRawComponent,
  SimpleConfirmData
} from '../../core/components/confirm-dialog-raw/confirm-dialog-raw.component';
import { LocalStorageService } from '../../core/services/local-storage.service';

@Component({
  selector: 'pixl-main-global-metadata',
  imports: [
    MatSortModule,
    MatIconModule,
    MatTableModule,
    MatButtonModule,
    FreeFilterInputComponent,
    AsyncPipe,
    MatPaginator,
    MatTooltip,
  ],
  templateUrl: './main-global-metadata.component.html',
  styleUrl: './main-global-metadata.component.scss'
})

export class MainGlobalMetadataComponent implements OnInit, AfterViewInit {
  private store = inject(Store);
  private widgetService = inject(WidgetMessageService);
  private dialog = inject(MatDialog);
  private refresh$ = new BehaviorSubject<void>(undefined);
  private globalMetaDataService = inject(GlobalMetaDataEntityService);
  private freeFilter = '';
  private localStorageService = inject(LocalStorageService);

  globalMetaData$!: Observable<GlobalMetaDataEntity[]>;

  deletedIds$ = new BehaviorSubject<string[]>([]);

  public readonly freeFilterLen = 3;
  public currentSelectedRow = signal<string | undefined>(undefined);
  public readonly coloredButtonsEnabled = this.localStorageService.coloredButtonsEnabled;

  displayedColumns: string[] = ['actions', 'id', 'name', 'description', 'fieldConfigName', 'fieldConfigDatatype'];

  @ViewChild(MatPaginator) paginator!: MatPaginator;

  pageSize = 25;
  pageIndex = 0;
  totalItems = 1000;

  private pageChange$ = new BehaviorSubject<{ pageIndex: number, pageSize: number }>({ pageIndex: this.pageIndex, pageSize: this.pageSize });

  async ngOnInit() {
    this.globalMetaData$ = combineLatest([
      this.pageChange$,
      this.refresh$,
      this.deletedIds$
    ]).pipe(
      switchMap(([{ pageIndex, pageSize }, , deletedIds]) => {
        const query: Record<string, string | number> = {
          'page[size]': pageSize,
          'page[number]': pageIndex + 1,
          'sort': 'name'
        };

        if (this.freeFilter?.trim()?.length >= 3) {
          query['page[number]'] = 0;
          query['filter[nameLike]'] = this.freeFilter.trim();
        }

        return from(this.globalMetaDataService.ensureLoaded(query)).pipe(
          switchMap(() => this.store.select(this.globalMetaDataService.allByQuery(query))),
          map((tests: GlobalMetaDataEntity[]) => tests.filter(m => !deletedIds.includes(m.id))),
          tap(() => {
            const state = this.store.selectSnapshot(this.globalMetaDataService.stateClass) as EntityStateModel<GlobalMetaDataEntity>;
            const queryKey = makeQueryKey(query);
            this.totalItems = state.meta?.[queryKey]?.page?.total ?? 0;
          })
        );
      })
    );
  }

  ngAfterViewInit() {
    this.paginator.page.subscribe((event: PageEvent) => {
      this.pageChange$.next({ pageIndex: event.pageIndex, pageSize: event.pageSize });
    });
  }

  add(): void {
    const dialogRef = this.dialog.open(FormDialogGlobalMetadataComponent, {
      width: '1200px',
      maxWidth: 'none',
      data: {}
    });

    dialogRef.afterClosed().subscribe(async (result: Partial<GlobalMetaDataEntity> | undefined) => {
      if (result) {
        const newEntityInstance = Object.assign(new GlobalMetaDataEntity(), result);

        await this.globalMetaDataService.create(newEntityInstance);
        this.store.dispatch(new InvalidateLoadedQueries());
        this.refresh$.next();

        this.widgetService.setMessage({
          message: 'Globale Metadaten erfolgreich erstellt',
          messageTye: 'mtSuccess'
        });
      }
    });
  }

  edit(globalMetaData: GlobalMetaDataEntity) {
    this.currentSelectedRow.set(globalMetaData.id);
    const dialogRef = this.dialog.open(FormDialogGlobalMetadataComponent, {
      width: '1200px',
      maxWidth: 'none',
      data: { ...globalMetaData }
    });

    dialogRef.afterClosed().subscribe(async (updatedGlobalMetaData: GlobalMetaDataEntity | undefined) => {
      if (!updatedGlobalMetaData) return;
      await firstValueFrom(this.globalMetaDataService.update(updatedGlobalMetaData));
      this.refresh$.next();

      this.widgetService.setMessage({
        message: 'Globale Metadaten erfolgreich editiert',
        messageTye: 'mtSuccess'
      });
    });
  }

  async delete(globalMetaData: GlobalMetaDataEntity) {
    if (!globalMetaData?.id) {
      console.error('ID is undefined!', globalMetaData);
      return;
    }

    this.currentSelectedRow.set(globalMetaData.id);

    const confirmed = await firstValueFrom(
      this.dialog
        .open<ConfirmDialogRawComponent, SimpleConfirmData, boolean>(ConfirmDialogRawComponent, {
          data: {
            name: String(globalMetaData.name),
            headerText: 'Globale Metadaten löschen',
            afterNameText: 'wirklich löschen?'
          }
        })
        .afterClosed()
    );

    if (!confirmed) return;

    await firstValueFrom(this.globalMetaDataService.delete(globalMetaData.id));

    this.store.dispatch(new InvalidateLoadedQueries());
    this.refresh$.next();

    this.widgetService.setMessage({
      message: 'Globale Metadaten erfolgreich gelöscht.',
      messageTye: 'mtSuccess'
    });
  }

  public getSearchFilterHint(): string {
    return getMinFilterLengthText(this.freeFilterLen);
  }

  public setFreeFilter(input: string): void {
    this.currentSelectedRow.set(undefined);
    this.freeFilter = input;
    this.refresh$.next();
  }

  trackById(index: number, globalMetaData: GlobalMetaDataEntity): string {
    return globalMetaData.id;
  }
}
